import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  Badge,
  Button,
  Card,
  Field,
  FormRow,
  Input,
  KanbanBoard,
  RightDrawer,
} from '@projexn/ui';
import {
  TENDER_STAGES,
  TENDER_STAGE_LABELS,
  type TenderStage,
} from '@projexn/shared';
import { api } from '../lib/api';

interface Tender {
  id: string;
  name: string;
  stage: TenderStage;
  valueCents: number | null;
  dueDate: string | null;
  clientId: string | null;
  metadata?: Record<string, unknown>;
}

function refOf(t: Tender): string {
  const r = t.metadata?.reference;
  return typeof r === 'string' ? r : t.id.slice(-6).toUpperCase();
}
function valueOf(t: Tender): string {
  if (t.valueCents == null) return '';
  return `$${(t.valueCents / 100).toLocaleString()}`;
}

interface Client {
  id: string;
  name: string;
}

const STAGE_ACCENTS: Record<TenderStage, 'blue' | 'amber' | 'green' | 'zinc' | 'red'> = {
  lead: 'zinc',
  invited: 'blue',
  estimating: 'amber',
  submitted: 'blue',
  shortlisted: 'amber',
  won: 'green',
  lost: 'red',
  withdrawn: 'zinc',
};

export function PipelinePage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);

  const tendersQ = useQuery({
    queryKey: ['tenders'],
    queryFn: () => api.get<{ items: Tender[] }>('/api/tenders'),
  });

  const clientsQ = useQuery({
    queryKey: ['clients'],
    queryFn: () => api.get<{ items: Client[] }>('/api/clients'),
  });

  const columns = useMemo(() => {
    const items = tendersQ.data?.items ?? [];
    return TENDER_STAGES.map((stage) => ({
      id: stage,
      title: TENDER_STAGE_LABELS[stage],
      accent: STAGE_ACCENTS[stage],
      items: items.filter((t) => t.stage === stage),
    }));
  }, [tendersQ.data]);

  const advance = useMutation({
    mutationFn: ({ id, stage }: { id: string; stage: TenderStage }) =>
      api.patch<Tender>(`/api/tenders/${id}/stage`, { stage }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenders'] }),
  });

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Tender Pipeline</h1>
          <p className="text-sm text-zinc-500">Live across all members on this tenant.</p>
        </div>
        <Button onClick={() => setCreating(true)}>New tender</Button>
      </header>

      {tendersQ.isLoading ? (
        <Card className="p-6 text-sm text-zinc-500">Loading…</Card>
      ) : tendersQ.isError ? (
        <Card className="p-6 text-sm text-red-700">
          {(tendersQ.error as Error).message}
        </Card>
      ) : (tendersQ.data?.items ?? []).length === 0 ? (
        <Card className="p-12 text-center">
          <p className="text-sm text-zinc-500">No tenders yet.</p>
          <Button className="mt-4" onClick={() => setCreating(true)}>
            Create your first tender
          </Button>
        </Card>
      ) : (
        <div className="h-[calc(100vh-12rem)]">
          <KanbanBoard
            columns={columns}
            itemKey={(t) => t.id}
            onItemClick={(t) => navigate({ to: '/tenders/$tenderId', params: { tenderId: t.id } })}
            renderItem={(t) => (
              <div className="space-y-1">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-medium">{t.name}</p>
                  <Badge>{refOf(t)}</Badge>
                </div>
                {valueOf(t) && (
                  <p className="text-xs text-zinc-700">{valueOf(t)}</p>
                )}
                <div className="flex gap-1 pt-1">
                  {TENDER_STAGES.filter((s) => s !== t.stage)
                    .slice(0, 2)
                    .map((next) => (
                      <button
                        key={next}
                        onClick={(e) => {
                          e.stopPropagation();
                          advance.mutate({ id: t.id, stage: next });
                        }}
                        className="rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] text-zinc-600 hover:bg-zinc-200"
                      >
                        → {TENDER_STAGE_LABELS[next]}
                      </button>
                    ))}
                </div>
              </div>
            )}
          />
        </div>
      )}

      <RightDrawer open={creating} onClose={() => setCreating(false)} title="New tender">
        <NewTenderForm
          clients={clientsQ.data?.items ?? []}
          onCreated={() => {
            setCreating(false);
            qc.invalidateQueries({ queryKey: ['tenders'] });
          }}
        />
      </RightDrawer>
    </div>
  );
}

interface NewTenderInput {
  name: string;
  clientId?: string;
  valueCents?: number;
  dueDate?: string;
  metadata?: Record<string, unknown>;
}

function NewTenderForm({
  clients,
  onCreated,
}: {
  clients: Client[];
  onCreated: () => void;
}) {
  const [form, setForm] = useState<{
    reference: string;
    name: string;
    clientId?: string;
    estimatedValue?: number;
    dueAt?: string;
  }>({ reference: '', name: '' });
  const mutate = useMutation({
    mutationFn: () =>
      api.post<Tender>('/api/tenders', {
        name: form.name,
        clientId: form.clientId,
        valueCents:
          form.estimatedValue !== undefined ? Math.round(form.estimatedValue * 100) : undefined,
        dueDate: form.dueAt ? new Date(form.dueAt).toISOString() : undefined,
        metadata: form.reference ? { reference: form.reference } : undefined,
      } satisfies NewTenderInput),
    onSuccess: onCreated,
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        mutate.mutate();
      }}
      className="space-y-4"
    >
      <FormRow>
        <Field label="Reference">
          <Input
            value={form.reference}
            onChange={(e) => setForm({ ...form, reference: e.target.value })}
            placeholder="TND-0001"
          />
        </Field>
      </FormRow>
      <FormRow>
        <Field label="Name" required>
          <Input
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </Field>
      </FormRow>
      <FormRow>
        <Field label="Client">
          <select
            value={form.clientId ?? ''}
            onChange={(e) => setForm({ ...form, clientId: e.target.value || undefined })}
            className="h-9 w-full rounded-md border border-zinc-300 bg-white px-3 text-sm"
          >
            <option value="">— none —</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Estimated value (AUD)">
          <Input
            type="number"
            min={0}
            value={form.estimatedValue ?? ''}
            onChange={(e) =>
              setForm({
                ...form,
                estimatedValue: e.target.value === '' ? undefined : Number(e.target.value),
              })
            }
          />
        </Field>
      </FormRow>
      <FormRow>
        <Field label="Due date">
          <Input
            type="date"
            value={form.dueAt ?? ''}
            onChange={(e) => setForm({ ...form, dueAt: e.target.value || undefined })}
          />
        </Field>
      </FormRow>
      {mutate.isError && (
        <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
          {(mutate.error as Error).message}
        </p>
      )}
      <div className="flex justify-end gap-2 pt-2">
        <Button type="submit" loading={mutate.isPending}>
          Create
        </Button>
      </div>
    </form>
  );
}
